import type { Language } from "@heredajo/schema";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getCurrentUserEmail, hasSessionCookie, signOut } from "../lib/auth.js";
import { t } from "../lib/strings.js";

interface Props {
  lang: Language;
}

function initials(email: string): string {
  const local = email.split("@")[0] ?? email;
  const first = local[0]?.toUpperCase() ?? "?";
  const dot = local.indexOf(".");
  if (dot > 0 && dot < local.length - 1) {
    const second = local[dot + 1]?.toUpperCase() ?? "";
    return `${first}${second}`;
  }
  return first;
}

export function UserMenu({ lang }: Props): JSX.Element | null {
  const s = t(lang);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Re-check the session on every route change so the avatar disappears
  // immediately after sign-out (the component lives in the header and stays
  // mounted across navigations, so a once-on-mount fetch would go stale).
  useEffect(() => {
    if (!hasSessionCookie()) {
      setEmail(null);
      return;
    }
    let cancelled = false;
    getCurrentUserEmail().then((e) => {
      if (!cancelled) setEmail(e);
    });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!email) return null;

  const onSignOut = async () => {
    setOpen(false);
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={s.signedInAs(email)}
        title={s.signedInAs(email)}
        className="h-8 w-8 rounded-full flex items-center justify-center bg-ink text-parchment-100 text-xs font-semibold ring-1 ring-parchment-300/40 hover:ring-parchment-300 focus:outline-none focus:ring-2 focus:ring-parchment-300"
      >
        {initials(email)}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-64 bg-white border border-parchment-200 rounded-md shadow-lg text-ink overflow-hidden z-10"
        >
          <div className="px-4 py-3 border-b border-parchment-200">
            <div className="text-sm break-all">{email}</div>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={onSignOut}
            className="w-full text-left px-4 py-2 text-sm hover:bg-parchment-100"
          >
            {s.signOut}
          </button>
        </div>
      )}
    </div>
  );
}
